import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Poc,
  AiOpportunity,
  DataSource,
} from '../../features/FunctionalDesignXPLR/FunctionalRequirements/interfaces';
import {
  functionalRequirementsDefaultData,
  integratedRequirementsDefaultData,
  noFunctionalRequirementsDefaultData,
  pocRequirementsDefaultData,
  pocStatusFieldDefaultData,
} from './functionalRequirementsDefaultData';

const initialState: AiOpportunity = {
  id: '',
  name: '',
  description: '',
  checked: false,
  valid: false,
  data: {
    Vendor: [],
    Embedded: '',
    mustHave: '',
    complexity: 0,
    knowledgeData: [],
    'PoC Complexity': '',
    integrationRequirement: '',
  },
  libraryData: {},
  pocs: [
    {
      id: '',
      title: '',
      createdAt: 0,
      updatedAt: 0,
      approved: false,
      deploymentHandshake: {},
      description: '',
      functionalRequirements: functionalRequirementsDefaultData,
      genAiChecklist: {},
      generalInformation: {
        source: {
          confidence: 0,
          ease: 0,
          impact: 0,
          'ice-score': 0,
        },
        complexity: '',
        typeImpact: '',
        opportunitySource: '',
        status: '',
        result: '',
      },
      noFunctionalRequirements: noFunctionalRequirementsDefaultData,
      inputsDataSource: [],
      pocRequirements: pocRequirementsDefaultData,
      integratedRequirements: integratedRequirementsDefaultData,
      effortAndCostEstimation: {},
      pocStatusFields: pocStatusFieldDefaultData,
    },
  ],
};

export const opportunitySlice = createSlice({
  name: 'opportunitySlice',
  initialState,
  reducers: {
    updateDataInsight: (_state, action: PayloadAction<Partial<Poc>>) => {
      return {
        ...initialState,
        ...action.payload,
      };
    },

    updateRequirementsData: (state, action: PayloadAction<AiOpportunity>) => {
      return {
        ...state,
        ...action.payload,
      };
    },

    // Reset Action
    // resetDataInsight: () => initialState,

    updateGeneralInfo: (
      state,
      action: PayloadAction<
        Partial<{ name: string; description: string; typeOfImpact: string; status: string }>
      >
    ) => {
      const { name, description, typeOfImpact, status } = action.payload;

      if (name !== undefined) {
        state.name = name;
      }

      if (description !== undefined) {
        state.description = description;
      }

      if (typeOfImpact !== undefined && state.pocs?.[0]?.generalInformation) {
        state.pocs[0].generalInformation.typeImpact = typeOfImpact;
      }

      if (status !== undefined) {
        state.pocs[0].generalInformation.status = status;
      }
    },

    updateScore: (
      state,
      action: PayloadAction<{ type: 'impact' | 'confidence' | 'ease'; value: number }>
    ) => {
      const { type, value } = action.payload;

      if (state.pocs?.[0]?.generalInformation?.source) {
        state.pocs[0].generalInformation.source[type] = value;

        const { impact, confidence, ease } = state.pocs[0].generalInformation.source;
        const iceScore = Math.round(((impact + confidence + ease) / 300) * 1000);
        state.pocs[0].generalInformation.source['ice-score'] = iceScore;
      }
    },

    setApprovalStatus: (state, action: PayloadAction<boolean>) => {
      // Use the existing 'approved' field in the poc
      console.log('Approving:', action.payload);

      if (state.pocs?.[0]) {
        state.pocs[0].approved = action.payload;
      }
    },

    addInputsDataSource: (state, action: PayloadAction<DataSource>) => {
      if (state.pocs?.[0]) {
        state.pocs[0].inputsDataSource = state.pocs[0].inputsDataSource || [];
        state.pocs[0].inputsDataSource.push(action.payload);
      }
    },

    updateInputsDataSource: (state, action: PayloadAction<DataSource>) => {
      if (state.pocs?.[0]?.inputsDataSource) {
        const index = state.pocs[0].inputsDataSource.findIndex((ds) => ds.id === action.payload.id);
        if (index !== -1) {
          state.pocs[0].inputsDataSource[index] = action.payload;
        }
      }
    },

    deleteInputsDataSource: (state, action: PayloadAction<string>) => {
      if (state.pocs?.[0]?.inputsDataSource) {
        state.pocs[0].inputsDataSource = state.pocs[0].inputsDataSource.filter(
          (ds) => ds.id !== action.payload
        );
      }
    },

    clearAllInputsDataSources: (state) => {
      if (state.pocs?.[0]) {
        state.pocs[0].inputsDataSource = [];
      }
    },

    syncApprovalStatus: (
      state,
      action: PayloadAction<{
        componentType: keyof typeof pocStatusFieldDefaultData;
        value: boolean;
      }>
    ) => {
      const { componentType, value } = action.payload;
      if (state?.pocs?.[0]?.pocStatusFields) {
        state.pocs[0].pocStatusFields[componentType] = value;
      }
    },

    setDeploymentHandshake: (state, action: PayloadAction<Record<string, string>>) => {
      if (state.pocs?.[0]) {
        state.pocs[0].deploymentHandshake = action.payload;
      }
    },
  },
});

export const {
  updateDataInsight,
  updateRequirementsData,
  // resetDataInsight,
  updateGeneralInfo,
  updateScore,
  setApprovalStatus,
  // setSubmittingStatus,
  addInputsDataSource,
  updateInputsDataSource,
  deleteInputsDataSource,
  clearAllInputsDataSources,
  syncApprovalStatus,
  setDeploymentHandshake,
} = opportunitySlice.actions;

export default opportunitySlice.reducer;
